import { useEffect, useRef, useState } from "react";
import { Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/CaseBits";

/** Case-level thread for the tribunal and counsel; every message is audited. */
export const CaseMessages = ({ caseId }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/cases/${caseId}/messages`).then(({ data }) => setMsgs(data)).catch(() => setMsgs([]));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/cases/${caseId}/messages`, { text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Section title="Case messages — tribunal & counsel" testId="messages-section">
      <p className="px-6 pt-4 text-xs text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Formal communications happen here, not on WhatsApp. Timestamped and recorded in the case audit trail; parties' clients do not see this thread.</p>
      <div className="px-6 py-4 space-y-3 max-h-[28rem] overflow-y-auto" data-testid="messages-list">
        {msgs === null ? <div className="h-16 animate-pulse bg-muted" /> : msgs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6" data-testid="messages-empty">No messages yet.</p>
        ) : msgs.map((m) => {
          const mine = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm ${mine ? "bg-blue-900 text-white" : "bg-muted"}`}>
                <p className={`text-[10px] uppercase tracking-widest ${mine ? "text-blue-200" : "text-muted-foreground"}`}>{m.user_name} · {m.role} · {fmtDateTime(m.at)}</p>
                <p className="mt-1 whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="px-6 pb-5 flex gap-2" data-testid="message-form">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write to the tribunal and counsel…" data-testid="message-input"
          className="flex-1 h-10 px-3 border border-border bg-background text-sm rounded-none" />
        <button type="submit" data-testid="message-send" className="inline-flex items-center h-10 px-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
          <Send className="h-3.5 w-3.5 mr-2" /> Send
        </button>
      </form>
    </Section>
  );
};

const ACTION_LABEL = {
  document_upload: "Document uploaded", payment: "Payment recorded", order_passed: "Order passed", signed_pdf: "Signed PDF attached",
  arbitrator_assigned: "Arbitrator assigned", arbitrator_accepted: "Arbitrator accepted", arbitrator_declined: "Arbitrator declined",
  hearing_scheduled: "Sitting scheduled", hearing_rescheduled: "Sitting moved", hearing_cancelled: "Sitting cancelled",
  status_change: "Stage changed", interim_order: "Interim order", message: "Message sent", bulk_download: "Case file downloaded",
};

export const AuditTrail = ({ caseId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/cases/${caseId}/audit`).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [caseId]);
  return (
    <Section title="Audit trail" testId="audit-section">
      {rows === null ? <div className="h-16 m-6 animate-pulse bg-muted" /> : rows.length === 0 ? (
        <p className="px-6 py-8 text-sm text-muted-foreground" data-testid="audit-empty">No actions recorded yet.</p>
      ) : (
        <ol className="px-6 py-4 divide-y divide-border" data-testid="audit-list">
          {rows.map((r) => (
            <li key={r.id} className="py-3 grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-2 text-sm" data-testid={`audit-${r.id}`}>
              <span className="font-mono text-xs text-muted-foreground">{fmtDateTime(r.at)}</span>
              <span><span className="font-medium">{ACTION_LABEL[r.action] || r.action}</span> — {r.detail}<span className="block text-xs text-muted-foreground">{r.user_name} · {r.role}</span></span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
};
